import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from './environment';
import { WorksuiteAdminAppConfig } from './worksuite-admin-config.interface';

const CACHE_STORAGE_KEY = 'worksuite_admin_config';
const LOAD_TIMEOUT_MS = 5000;

/**
 * Stellt die im worksuite-admin gepflegte **Modul-Aktivierung** (aktiv/inaktiv)
 * bereit. Wird via APP_INITIALIZER beim Start geladen.
 *
 * Bewusst KEINE frei-formige Runtime-Config und KEIN Merge ins `environment`:
 * die Werte im Code/Environment bleiben die alleinige Quelle. Der worksuite-admin
 * steuert ausschliesslich, welche Module aktiv sind.
 *
 * Bezug über die **eigene** Work-Suite-API (`{apiConfiguration.baseUrl}/v2/app-config`),
 * die serverseitig vom worksuite-admin lädt → der Shared-Key gelangt nie ins
 * Browser-Bundle.
 *
 * Resilienz: Start liest zuerst den `localStorage`-Cache (sofort, auch offline);
 * der Abruf hat ein Timeout und blockiert den Start nie – schlägt er fehl, bleibt
 * die gecachte Modul-Liste aktiv.
 */
@Injectable({ providedIn: 'root' })
export class WorksuiteAdminConfigService {
  private readonly http = inject(HttpClient);
  private readonly appConfig = signal<WorksuiteAdminAppConfig | null>(this.readCache());

  public readonly environmentName = computed(() => this.appConfig()?.environment);
  public readonly enabledModules = computed<string[]>(() => this.appConfig()?.modules?.enabled ?? []);
  public readonly disabledModules = computed<string[]>(() => this.appConfig()?.modules?.disabled ?? []);

  public async load(): Promise<void> {
    const baseUrl = environment.apiConfiguration?.baseUrl;
    if (!baseUrl) {
      return;
    }
    try {
      const result = await firstValueFrom(
        this.http
          .get<WorksuiteAdminAppConfig>(`${baseUrl.replace(/\/$/, '')}/v2/app-config`)
          .pipe(timeout(LOAD_TIMEOUT_MS)),
      );
      this.appConfig.set(result);
      this.writeCache(result);
    } catch (error) {
      // Nicht fatal: die App läuft mit der (ggf. gecachten) letzten Modul-Liste weiter.
      console.warn('Modul-Konfiguration konnte nicht geladen werden – gecachte Liste bleibt aktiv.', error);
    }
  }

  public isModuleEnabled(moduleKey: string): boolean {
    return this.enabledModules().includes(moduleKey);
  }

  /**
   * true, wenn das Modul im worksuite-admin ausdrücklich deaktiviert ist. Ohne geladene
   * Modul-Liste (Admin nicht erreichbar, kein Cache) liefert es false — die Funktion bleibt sichtbar.
   */
  public isModuleDisabled(moduleKey: string): boolean {
    return this.disabledModules().includes(moduleKey);
  }

  /**
   * Für `data.requiredModule` an Routen: ein einzelner Key oder eine Liste, von der mindestens ein Modul aktiv sein
   * muss (z. B. der Bereich Akquise, der Reservationen und Akquiseobjekte bündelt). Gesperrt erst, wenn alle aus sind.
   */
  public isRequiredModuleDisabled(requiredModule: string | string[] | undefined): boolean {
    if (!requiredModule || (Array.isArray(requiredModule) && requiredModule.length === 0)) {
      return false;
    }
    return (Array.isArray(requiredModule) ? requiredModule : [requiredModule]).every((moduleKey) => this.isModuleDisabled(moduleKey));
  }

  // --- Last-known-good localStorage-Cache ---------------------------------

  private readCache(): WorksuiteAdminAppConfig | null {
    try {
      const raw = localStorage.getItem(CACHE_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as WorksuiteAdminAppConfig) : null;
    } catch {
      return null;
    }
  }

  private writeCache(config: WorksuiteAdminAppConfig): void {
    try {
      localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(config));
    } catch {
      // localStorage nicht verfügbar (z.B. Privatmodus) – ignorieren.
    }
  }
}
